import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { CandlestickSeries, createChart, LineSeries } from "lightweight-charts";
import { del, get, patch, post, setCsrfToken } from "./api";
import { money, orderEstimate, percentage, percentagePoints } from "./domain";
import FullChart from "./charting/FullChart";
import "./styles.css";
import "./charts.css";

const basename =
  import.meta.env.BASE_URL === "/"
    ? undefined
    : import.meta.env.BASE_URL.replace(/\/$/, "");
const compact = (n) =>
  new Intl.NumberFormat("en-GB", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(n) || 0);
const date = (v) =>
  v
    ? new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(v))
    : "Open date";
const symbols = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD", "ETHUSD"];
const isTempWatchlist = (id) => String(id).startsWith("temp-");
const shortId = (value) => {
  const text = String(value ?? "");
  return text.length > 16 ? `${text.slice(0, 8)}…${text.slice(-4)}` : text;
};
const timeAgo = (value) => {
  const seconds = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};
const marketNames = new Map();
const icons = {
  home: "M3 11 12 3l9 8M5 10v11h14V10M9 21v-7h6v7",
  probability: "M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm0 4v5l3 2",
  trade: "M4 18l5-6 4 3 7-9m-5 0h5v5",
  chart: "M4 20V10m5 7V4m5 16v-8m5 3V7",
  signal: "M3 12h4l2-6 4 12 2-6h6",
  calendar: "M4 6h16v15H4zm4-3v5m8-5v5M4 11h16",
  milo: "M4 6h16v11H9l-5 4zm4 4h8m-8 3h5",
  search: "M16 16l5 5m-3-11a8 8 0 1 1-16 0 8 8 0 0 1 16 0",
  bell: "M18 9a6 6 0 0 0-12 0c0 6-3 6-3 8h18c0-2-3-2-3-8m-8 12h4",
  menu: "M4 7h16M4 12h16M4 17h16",
  arrow: "m9 18 6-6-6-6",
  spark: "m12 3 2 5 5 2-5 2-2 5-2-5-5-2 5-2z",
  star: "m12 3.5 2.7 5.5 6 .9-4.3 4.2 1 5.9-5.4-2.8-5.4 2.8 1-5.9-4.3-4.2 6-.9z",
};
function Icon({ name, filled = false }) {
  return (
    <svg viewBox="0 0 24 24">
      <path
        d={icons[name] || icons.home}
        fill={filled ? "currentColor" : "none"}
      />
    </svg>
  );
}
function Logo({ small = false }) {
  return (
    <Link to="/" className={`brand ${small ? "small" : ""}`}>
      <span>C</span>
      {!small && <strong>CapDeskPro</strong>}
    </Link>
  );
}

function PublicHome({ bootstrap }) {
  return (
    <main className="public">
      <nav className="public-nav">
        <Logo />
        <div>
          <a href="#platform">Platform</a>
          <a href="#intelligence">Intelligence</a>
          <a href="#workflow">Workflow</a>
        </div>
        <section>
          <Link to="/login">Log in</Link>
          <Link
            className="btn dark"
            to={bootstrap.authenticated ? "/dashboard" : "/register"}
          >
            Start free
          </Link>
        </section>
      </nav>
      <div className="hero">
        <article>
          <span className="eyebrow">
            MARKET INTELLIGENCE FOR BETTER DECISIONS
          </span>
          <h1>Probability changes how you trade.</h1>
          <p>
            Connect prediction markets with price data, research and controlled
            paper trading. See the event. Understand the effect. Test the
            decision.
          </p>
          <div>
            <Link
              className="btn blue"
              to={bootstrap.authenticated ? "/dashboard" : "/register"}
            >
              Open your intelligence desk
            </Link>
            <a href="#platform">
              Explore the platform <Icon name="arrow" />
            </a>
          </div>
        </article>
        <TerminalPreview />
      </div>
      <div id="workflow" className="proof">
        <span>One workspace</span>
        <b>Prediction markets</b>
        <b>Dukascopy price data</b>
        <b>AI research</b>
        <b>Paper execution</b>
      </div>
      <section id="platform" className="public-section">
        <header>
          <span className="eyebrow">A COMPLETE DECISION WORKSPACE</span>
          <h2>From market expectation to measured action.</h2>
        </header>
        <div className="features">
          {[
            [
              "01",
              "Probability Engine",
              "Track market expectations. Compare them with the momentum estimate. Find meaningful gaps.",
            ],
            [
              "02",
              "Market Intelligence",
              "Link each event to FX, gold, crypto and indices. Review price structure in context.",
            ],
            [
              "03",
              "Controlled Execution",
              "Test the idea with paper capital. Record decisions. Measure the result.",
            ],
          ].map((x) => (
            <article key={x[0]}>
              <small>{x[0]}</small>
              <h3>{x[1]}</h3>
              <p>{x[2]}</p>
            </article>
          ))}
        </div>
      </section>
      <section id="intelligence" className="statement">
        <span className="eyebrow">INTELLIGENT TRADING</span>
        <h2>One clear view of probability, evidence and risk.</h2>
        <p>
          Milo and trading agents organise research. Fixed controls keep every
          decision accountable.
        </p>
        <Link
          className="btn light"
          to={bootstrap.authenticated ? "/dashboard" : "/register"}
        >
          Start with paper capital
        </Link>
      </section>
      <footer className="public-footer">
        <Logo />
        <p>Probability-led market intelligence.</p>
        <span>© 2026 CapDeskPro</span>
      </footer>
    </main>
  );
}
function TerminalPreview() {
  let rows = [
    ["Federal Reserve cut before December", "64%", "+6.1%"],
    ["Bitcoin above $150,000 this year", "31%", "+3.7%"],
    ["Gold above $4,000 before year-end", "47%", "-1.9%"],
  ];
  return (
    <div className="preview">
      <header>
        <Logo small />
        <span>
          <Icon name="search" /> Search markets and symbols
        </span>
        <b>LIVE DESK</b>
      </header>
      <div className="preview-body">
        <aside>
          {["O", "P", "C", "A"].map((x, i) => (
            <i className={i === 1 ? "active" : ""} key={i}>
              {x}
            </i>
          ))}
        </aside>
        <main>
          <h3>
            <span>
              OPPORTUNITY BOARD<small>Markets in motion</small>
            </span>
            <b>Simulation</b>
          </h3>
          {rows.map((r, i) => (
            <div className="preview-row" key={r[0]}>
              <span>
                <i>{i ? "CRYPTO" : "MACRO"}</i>
                {r[0]}
              </span>
              <b>{r[1]}</b>
              <em className={r[2][0] === "+" ? "up" : "down"}>{r[2]}</em>
            </div>
          ))}
          <svg viewBox="0 0 620 130" preserveAspectRatio="none">
            <path
              className="area"
              d="M0 112C80 108 95 75 150 88s105 5 145-28 100 25 150-10 100 2 175-40v120H0Z"
            />
            <path d="M0 112C80 108 95 75 150 88s105 5 145-28 100 25 150-10 100 2 175-40" />
          </svg>
        </main>
      </div>
    </div>
  );
}

function Field({ label, optional, ...p }) {
  return (
    <label className="field">
      <span>
        {label}
        {optional && <em>Optional</em>}
      </span>
      <input
        required={!optional}
        minLength={p.type === "password" ? 8 : undefined}
        {...p}
      />
    </label>
  );
}
function AuthPage({ mode, onAuthenticated }) {
  const nav = useNavigate();
  const [form, setForm] = useState(
    mode === "login"
      ? { identifier: "", password: "", remember: true }
      : {
          name: "",
          username: "",
          email: "",
          country: "",
          password: "",
          password_confirmation: "",
        },
  );
  const [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const update = (e) =>
    setForm((f) => ({
      ...f,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      let p = await post(
        mode === "login" ? "/frontend-api/login" : "/frontend-api/register",
        form,
      );
      if (mode === "login") {
        await onAuthenticated();
        nav("/dashboard");
      } else
        setMessage(p.message || "Check your email to activate your account.");
    } catch (x) {
      setError(x.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth">
      <header>
        <Logo />
        <Link to="/">Back to website</Link>
      </header>
      <section>
        <article>
          <span className="eyebrow">
            {mode === "login" ? "MEMBER ACCESS" : "START FREE"}
          </span>
          <h1>
            {mode === "login"
              ? "Welcome back."
              : "Build a better trading process."}
          </h1>
          <p>
            Use probability, evidence and controlled simulation in one
            professional workspace.
          </p>
          {[
            "Find events that can move your markets.",
            "Compare probability with the market momentum estimate.",
            "Test decisions before capital is at risk.",
          ].map((x, i) => (
            <div className="auth-point" key={x}>
              <b>0{i + 1}</b>
              {x}
            </div>
          ))}
        </article>
        <form onSubmit={submit}>
          <h2>
            {mode === "login" ? "Log in to CapDeskPro" : "Create your account"}
          </h2>
          <p>
            {mode === "login"
              ? "Enter your details to open the terminal."
              : "Start with a controlled paper account."}
          </p>
          {mode === "register" && (
            <>
              <Field
                label="Full name"
                name="name"
                value={form.name}
                onChange={update}
              />
              <Field
                label="Username"
                optional
                name="username"
                value={form.username}
                onChange={update}
              />
            </>
          )}
          <Field
            label={mode === "login" ? "Email or username" : "Email"}
            name={mode === "login" ? "identifier" : "email"}
            type={mode === "login" ? "text" : "email"}
            value={mode === "login" ? form.identifier : form.email}
            onChange={update}
          />
          {mode === "register" && (
            <Field
              label="Country"
              name="country"
              value={form.country}
              onChange={update}
            />
          )}
          <Field
            label="Password"
            name="password"
            type="password"
            value={form.password}
            onChange={update}
          />
          {mode === "register" && (
            <Field
              label="Confirm password"
              name="password_confirmation"
              type="password"
              value={form.password_confirmation}
              onChange={update}
            />
          )}{" "}
          {mode === "login" && (
            <div className="auth-options">
              <label className="check">
                <input
                  type="checkbox"
                  name="remember"
                  checked={form.remember}
                  onChange={update}
                />
                Keep me signed in
              </label>
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
          )}
          {error && <p className="form-error">{error}</p>}
          {message && <p className="form-success">{message}</p>}
          <button className="btn blue" disabled={busy}>
            {busy
              ? "Please wait"
              : mode === "login"
                ? "Log in"
                : "Create account"}
          </button>
          <footer>
            {mode === "login" ? (
              <>
                New to CapDeskPro? <Link to="/register">Start free</Link>
              </>
            ) : (
              <>
                Already a member? <Link to="/login">Log in</Link>
              </>
            )}
          </footer>
        </form>
      </section>
    </main>
  );
}

function PasswordPage({ mode }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const recoveryEmail = params.get("email") || "";
  const [form, setForm] = useState({
    identifier: "",
    email: recoveryEmail,
    code: "",
    password: "",
    password_confirmation: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (mode === "reset" && recoveryEmail) {
      setForm((current) => ({ ...current, email: recoveryEmail }));
    }
  }, [mode, recoveryEmail]);
  const update = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "forgot") {
        const result = await post("/frontend-api/forgot-password", {
          identifier: form.identifier,
        });
        const email = result.redirect
          ? new URL(result.redirect, window.location.origin).searchParams.get(
              "email",
            )
          : "";
        navigate(
          `/reset-password${email ? `?email=${encodeURIComponent(email)}` : ""}`,
        );
      } else {
        const result = await post("/frontend-api/reset-password", {
          email: form.email,
          code: form.code,
          password: form.password,
          password_confirmation: form.password_confirmation,
        });
        setMessage(result.message || "Password updated.");
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth password-page">
      <header>
        <Logo />
        <Link to="/login">Back to login</Link>
      </header>
      <section>
        <article>
          <span className="eyebrow">ACCOUNT RECOVERY</span>
          <h1>
            {mode === "forgot"
              ? "Reset your password."
              : "Choose a new password."}
          </h1>
          <p>A six-digit code will protect the account recovery process.</p>
        </article>
        <form onSubmit={submit}>
          <h2>
            {mode === "forgot"
              ? "Request a reset code"
              : "Enter your reset code"}
          </h2>
          {mode === "forgot" ? (
            <Field
              label="Email or username"
              name="identifier"
              value={form.identifier}
              onChange={update}
            />
          ) : (
            <>
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={update}
              />
              <Field
                label="Six-digit code"
                name="code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                value={form.code}
                onChange={update}
              />
              <Field
                label="New password"
                name="password"
                type="password"
                value={form.password}
                onChange={update}
              />
              <Field
                label="Confirm password"
                name="password_confirmation"
                type="password"
                value={form.password_confirmation}
                onChange={update}
              />
            </>
          )}
          {error && <p className="form-error">{error}</p>}
          {message && (
            <p className="form-success">
              {message} <Link to="/login">Log in</Link>
            </p>
          )}
          <button className="btn blue" disabled={busy}>
            {busy
              ? "Please wait"
              : mode === "forgot"
                ? "Send reset code"
                : "Update password"}
          </button>
        </form>
      </section>
    </main>
  );
}

const navItems = [
  ["/dashboard", "home", "Overview"],
  ["/prediction-markets", "probability", "Probability"],
  ["/paper-trading", "trade", "Paper trading"],
  ["/charts", "chart", "Charts"],
  ["/charts-old", "chart", "Charts (old)"],
  ["/signal-lab", "signal", "Signal Lab"],
  ["/economic-calendar", "calendar", "Calendar"],
  ["/milo", "milo", "Milo"],
];
function AlertBell({ inbox, setInbox }) {
  const [open, setOpen] = useState(false);
  const holder = useRef(null);
  useEffect(() => {
    if (!open) return;
    const away = (event) => {
      if (!holder.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", escape);
    };
  }, [open]);
  const markRead = async (ids) => {
    if (!ids.length) return;
    setInbox((current) => ({
      unread: Math.max(
        0,
        current.unread -
          current.items.filter((item) => ids.includes(item.id) && !item.read_at)
            .length,
      ),
      items: current.items.map((item) =>
        ids.includes(item.id) && !item.read_at
          ? { ...item, read_at: new Date().toISOString() }
          : item,
      ),
    }));
    try {
      const result = await post("/frontend-api/alerts/notifications/read", {
        ids,
      });
      setInbox((current) => ({ ...current, unread: result.unread }));
    } catch {
      // optimistic read state stands; the next poll resyncs the badge
    }
  };
  const unreadIds = inbox.items
    .filter((item) => !item.read_at)
    .map((item) => item.id);
  return (
    <span className="bell" ref={holder}>
      <button
        type="button"
        className={open ? "active" : ""}
        title="Probability alerts"
        aria-label={`Probability alerts, ${inbox.unread} unread`}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="bell" />
        {inbox.unread > 0 && <b>{inbox.unread > 99 ? "99+" : inbox.unread}</b>}
      </button>
      {open && (
        <div className="bell-menu">
          <header>
            <span>Probability alerts</span>
            {unreadIds.length > 0 && (
              <button type="button" onClick={() => markRead(unreadIds)}>
                Mark all read
              </button>
            )}
          </header>
          {inbox.items.length ? (
            inbox.items.map((item) => (
              <button
                type="button"
                className={`bell-item${item.read_at ? "" : " unread"}`}
                key={item.id}
                onClick={() => !item.read_at && markRead([item.id])}
              >
                <span>
                  <b>
                    {marketNames.get(String(item.market_id)) ||
                      shortId(item.market_id)}
                  </b>
                  <em>
                    {percentagePoints(item.probability)} · {timeAgo(item.triggered_at)}
                  </em>
                </span>
                {!item.read_at && <i />}
              </button>
            ))
          ) : (
            <p className="bell-empty">
              No alerts yet. Set one from any market row.
            </p>
          )}
        </div>
      )}
    </span>
  );
}
function Shell({ bootstrap, inbox, setInbox, children }) {
  const loc = useLocation(),
    go = useNavigate();
  const searchRef = useRef(null);
  const [q, setQ] = useState(""),
    [open, setOpen] = useState(false);
  useEffect(() => {
    const focusSearch = (event) => {
      if (
        event.key === "/" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(
          document.activeElement?.tagName,
        )
      ) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);
  useEffect(() => {
    const poll = window.setInterval(() => {
      get("/frontend-api/alerts/notifications")
        .then(setInbox)
        .catch(() => {});
    }, 60000);
    return () => window.clearInterval(poll);
  }, [setInbox]);
  const search = (e) => {
    e.preventDefault();
    let v = q.trim(),
      s = v.toUpperCase().replace(/[^A-Z]/g, "");
    if (v)
      go(
        symbols.includes(s)
          ? `/charts?symbol=${s}`
          : `/prediction-markets?search=${encodeURIComponent(v)}`,
      );
  };
  return (
    <div className="app">
      <aside className={`rail ${open ? "open" : ""}`}>
        <Logo small />
        <nav>
          {navItems.map((x) => (
            <Link
              className={loc.pathname === x[0] ? "active" : ""}
              to={x[0]}
              reloadDocument={x[0] === "/charts-old"}
              title={x[2]}
              key={x[0]}
              onClick={() => setOpen(false)}
            >
              <Icon name={x[1]} />
              <span>{x[2]}</span>
            </Link>
          ))}
        </nav>
        <b>{bootstrap.user.initials}</b>
      </aside>
      <main>
        <header className="topbar">
          <button className="mobile" onClick={() => setOpen((v) => !v)}>
            <Icon name="menu" />
          </button>
          <Link to="/dashboard" className="wordmark">
            CapDeskPro
          </Link>
          <form onSubmit={search}>
            <Icon name="search" />
            <input
              ref={searchRef}
              aria-label="Search symbols and prediction markets"
              placeholder="Search symbols, events or markets"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <kbd>/</kbd>
          </form>
          <nav>
            <Link to="/prediction-markets">Markets</Link>
            <Link to="/milo">Agents</Link>
            <Link to="/paper-trading">Portfolio</Link>
          </nav>
          <AlertBell inbox={inbox} setInbox={setInbox} />
          <details className="account">
            <summary>
              <span>{bootstrap.user.initials}</span>
              <div>
                <b>{bootstrap.user.name}</b>
                <em>{bootstrap.user.plan || "Trial access"}</em>
              </div>
            </summary>
            <div className="account-menu">
              <a href="/settings">Account settings</a>
              <a href="/payments">Billing history</a>
              {bootstrap.user.is_admin && <a href="/admin">Administration</a>}
              <form method="POST" action="/frontend-api/logout">
                <input
                  type="hidden"
                  name="_token"
                  value={bootstrap.csrf_token}
                />
                <button>Sign out</button>
              </form>
            </div>
          </details>
        </header>
        <nav className="categories">
          <span>Explore</span>
          <Link to="/prediction-markets">All markets</Link>
          <Link to="/prediction-markets?category=Macro">Macro</Link>
          <Link to="/prediction-markets?category=Crypto">Crypto</Link>
          <Link to="/prediction-markets?category=Commodities">Commodities</Link>
          <Link to="/charts?symbol=EURUSD">FX</Link>
          <Link to="/charts?symbol=XAUUSD">Gold</Link>
          <Link to="/signal-lab">Signals</Link>
        </nav>
        {children}
      </main>
    </div>
  );
}
function Loading() {
  return (
    <div className="loading">
      <i />
      <span>Preparing your intelligence desk</span>
    </div>
  );
}
function PageError({ message }) {
  return (
    <div className="page">
      <div className="page-error">
        <b>This view could not load.</b>
        <span>{message}</span>
        <button onClick={() => location.reload()}>Try again</button>
      </div>
    </div>
  );
}
function Mode({ board }) {
  return (
    <div className={`mode ${board.mode}`}>
      <i />
      <span>
        {board.mode === "live" ? "Live market data" : "Simulation data"}
      </span>
      <em>{board.provider}</em>
    </div>
  );
}
function Tags({ items = [] }) {
  return (
    <div className="tags">
      {items.map((x) => (
        <span key={x}>{x}</span>
      ))}
    </div>
  );
}
function MarketTable({
  markets,
  selected,
  onSelect,
  onTrade,
  watchlists,
  onWatch,
  alerts,
  alertsError,
  onCreateAlert,
  onToggleAlert,
  onDeleteAlert,
}) {
  return (
    <div className="table">
      <table>
        <thead>
          <tr>
            <th>Market</th>
            <th>Probability</th>
            <th>1 hour</th>
            <th>24 hours</th>
            <th>Volume</th>
            <th>Liquidity</th>
            <th>Linked assets</th>
            {onWatch && <th />}
            <th />
          </tr>
        </thead>
        <tbody>
          {markets.map((m) => (
            <tr
              className={selected?.id === m.id ? "selected" : ""}
              key={m.id}
              tabIndex="0"
              onClick={() => onSelect?.(m)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onSelect?.(m);
              }}
            >
              <td>
                <small>{m.category}</small>
                <b>{m.question}</b>
              </td>
              <td>
                <div className="prob">
                  <b>{percentage(m.yes_probability)}</b>
                  <i>
                    <em style={{ width: percentage(m.yes_probability) }} />
                  </i>
                </div>
              </td>
              <td className={m.change_1h >= 0 ? "up" : "down"}>
                {percentage(m.change_1h, true)}
              </td>
              <td className={m.change_24h >= 0 ? "up" : "down"}>
                {percentage(m.change_24h, true)}
              </td>
              <td>${compact(m.volume_24h)}</td>
              <td>${compact(m.liquidity)}</td>
              <td>
                <Tags items={m.related_assets} />
              </td>
              {onWatch && (
                <td>
                  <span className="row-actions">
                    <WatchToggle
                      market={m}
                      lists={watchlists || []}
                      onToggle={onWatch}
                    />
                    {onCreateAlert && (
                      <AlertToggle
                        market={m}
                        alerts={
                          alerts === null
                            ? null
                            : (alerts || []).filter(
                                (alert) =>
                                  String(alert.market_id) === String(m.id),
                              )
                        }
                        loadError={alertsError}
                        onCreate={onCreateAlert}
                        onToggle={onToggleAlert}
                        onDelete={onDeleteAlert}
                      />
                    )}
                  </span>
                </td>
              )}
              <td>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTrade(m);
                  }}
                >
                  Trade
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WatchToggle({ market, lists, onToggle }) {
  const [open, setOpen] = useState(false);
  const holder = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (event) => {
      if (!holder.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const watched = lists.some((list) =>
    list.items.some((item) => String(item.market_id) === String(market.id)),
  );
  return (
    <span
      className="watch-toggle"
      ref={holder}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={watched ? "watched" : ""}
        title={watched ? "Manage watchlists" : "Add to watchlist"}
        onClick={(event) => {
          event.stopPropagation();
          if (lists.length > 1) setOpen((value) => !value);
          else onToggle(market, lists[0] || null);
        }}
      >
        <Icon name="star" filled={watched} />
      </button>
      {open && (
        <div className="watch-menu">
          {lists.map((list) => {
            const inside = list.items.some(
              (item) => String(item.market_id) === String(market.id),
            );
            return (
              <button
                type="button"
                key={list.id}
                disabled={isTempWatchlist(list.id)}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggle(market, list);
                }}
              >
                <Icon name="star" filled={inside} />
                {list.name}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
function AlertToggle({
  market,
  alerts,
  loadError,
  onCreate,
  onToggle,
  onDelete,
}) {
  const [open, setOpen] = useState(false),
    [condition, setCondition] = useState("above"),
    [threshold, setThreshold] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const holder = useRef(null);
  useEffect(() => {
    if (!open) return;
    const away = (event) => {
      if (!holder.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", escape);
    };
  }, [open]);
  const armed = (alerts || []).some((alert) => alert.active);
  const submit = async (event) => {
    event.preventDefault();
    const value = Number(threshold);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      setError("Enter a probability between 0 and 100.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onCreate(market, condition, value);
      setThreshold("");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };
  const run = (action) => async () => {
    setError("");
    try {
      await action();
    } catch (requestError) {
      setError(requestError.message);
    }
  };
  return (
    <span
      className="alert-toggle"
      ref={holder}
      onKeyDown={(event) => {
        // let Escape bubble to the window listener that closes the popover
        if (event.key !== "Escape") event.stopPropagation();
      }}
    >
      <button
        type="button"
        className={armed ? "armed" : ""}
        title={armed ? "Manage probability alerts" : "Set a probability alert"}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <Icon name="bell" filled={armed} />
      </button>
      {open && (
        <div className="alert-pop" onClick={(event) => event.stopPropagation()}>
          <form onSubmit={submit}>
            <select
              aria-label="Alert condition"
              value={condition}
              onChange={(event) => setCondition(event.target.value)}
            >
              <option value="above">Rises above</option>
              <option value="below">Drops below</option>
            </select>
            <input
              aria-label="Threshold probability"
              inputMode="decimal"
              placeholder="0–100%"
              value={threshold}
              onChange={(event) => setThreshold(event.target.value)}
            />
            <button type="submit" disabled={busy || !threshold.trim()}>
              Set
            </button>
          </form>
          {error && <p className="form-error">{error}</p>}
          {loadError ? (
            <p className="alert-note">{loadError}</p>
          ) : alerts === null ? (
            <p className="alert-note">Loading alerts…</p>
          ) : (
            alerts.map((alert) => (
              <div className="alert-row" key={alert.id}>
                <span>
                  {alert.condition === "above" ? "Above" : "Below"}{" "}
                  {Number(alert.threshold)}%
                  <em>
                    {alert.active ? "Active" : "Paused"}
                    {alert.last_probability !== null &&
                      alert.last_probability !== undefined &&
                      ` · last ${percentagePoints(alert.last_probability)}`}
                    {!alert.active && alert.unavailable && " · market unavailable"}
                  </em>
                </span>
                <div>
                  <button type="button" onClick={run(() => onToggle(alert))}>
                    {alert.active ? "Pause" : "Resume"}
                  </button>
                  <button
                    type="button"
                    className="alert-delete"
                    onClick={run(() => onDelete(alert))}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </span>
  );
}
function WatchlistPanel({
  watchlists,
  markets,
  visible,
  onJump,
  onCreate,
  onRename,
  onDelete,
  onRemove,
  error,
}) {
  const [name, setName] = useState(""),
    [editing, setEditing] = useState(null),
    [draft, setDraft] = useState("");
  const byId = new Map(markets.map((m) => [String(m.id), m]));
  const visibleIds = new Set(visible.map((m) => String(m.id)));
  const submitCreate = (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    onCreate(name);
    setName("");
  };
  const submitRename = (event) => {
    event.preventDefault();
    if (draft.trim()) onRename(editing, draft);
    setEditing(null);
  };
  return (
    <section className="card watchlists">
      <header>
        <span>
          WATCHLISTS<b>Your tracked event markets</b>
        </span>
        <form onSubmit={submitCreate}>
          <input
            aria-label="New watchlist name"
            placeholder="New watchlist name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <button type="submit" disabled={!name.trim()}>
            Create
          </button>
        </form>
      </header>
      {error && <p className="form-error">{error}</p>}
      {watchlists.length ? (
        watchlists.map((list) => (
          <div className="watchlist" key={list.id}>
            <header>
              {editing?.id === list.id ? (
                <form onSubmit={submitRename}>
                  <input
                    aria-label="Rename watchlist"
                    autoFocus
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                  />
                  <button type="submit">Save</button>
                  <button type="button" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </form>
              ) : (
                <b>{list.name}</b>
              )}
              <span>
                <button
                  type="button"
                  disabled={isTempWatchlist(list.id)}
                  onClick={() => {
                    setEditing(list);
                    setDraft(list.name);
                  }}
                >
                  Rename
                </button>
                <button
                  type="button"
                  disabled={isTempWatchlist(list.id)}
                  onClick={() => onDelete(list)}
                >
                  Delete
                </button>
              </span>
            </header>
            <div className="watch-chips">
              {list.items.length ? (
                list.items.map((item) => {
                  const market = byId.get(String(item.market_id));
                  const jumpable =
                    market && visibleIds.has(String(market.id));
                  return (
                    <span className="watch-chip" key={item.market_id}>
                      <button
                        type="button"
                        className="watch-chip-name"
                        disabled={!jumpable}
                        title={
                          !market
                            ? "Not on the current market board"
                            : jumpable
                              ? "Show in the market table"
                              : "Hidden by the current filter"
                        }
                        onClick={() => jumpable && onJump(market)}
                      >
                        {market ? market.question : item.market_id}
                      </button>
                      <button
                        type="button"
                        className="watch-chip-remove"
                        aria-label={`Remove ${market ? market.question : item.market_id}`}
                        onClick={() => onRemove(list, item.market_id)}
                      >
                        ×
                      </button>
                    </span>
                  );
                })
              ) : (
                <em>No markets yet. Star a market in the table below.</em>
              )}
            </div>
          </div>
        ))
      ) : (
        <p className="watchlists-empty">
          Star any market below to start your first watchlist.
        </p>
      )}
    </section>
  );
}

function WorkChart({ market, defaultSymbol = "EURUSD" }) {
  const holder = useRef(null);
  const [tf, setTf] = useState("H1"),
    [symbol, setSymbol] = useState(defaultSymbol),
    [error, setError] = useState(""),
    [historyStatus, setHistoryStatus] = useState("Price only");
  useEffect(() => setSymbol(defaultSymbol), [defaultSymbol]);
  useEffect(() => {
    let s = market?.related_assets?.find((x) => symbols.includes(x));
    if (s) setSymbol(s);
  }, [market?.id]);
  useEffect(() => {
    let chart,
      observer,
      cancel = false;
    setError("");
    const historyRequest = market
      ? get(
          `/frontend-api/prediction-markets/${encodeURIComponent(market.id)}/history?interval=1w&fidelity=60`,
        ).catch(() => ({ mode: "unavailable", history: [] }))
      : Promise.resolve({ mode: "unavailable", history: [] });
    Promise.all([
      get(
        `/api/market-data/candles?symbol=${symbol}&timeframe=${tf}&limit=320`,
      ),
      historyRequest,
    ])
      .then(([p, probabilityPayload]) => {
        if (cancel) return;
        let rows = (Array.isArray(p) ? p : p.candles || p.data || [])
          .map((r) => ({
            time: Math.floor(new Date(r.start_at || r.time).getTime() / 1000),
            open: +r.open,
            high: +r.high,
            low: +r.low,
            close: +r.close,
          }))
          .filter((r) => Number.isFinite(r.time) && Number.isFinite(r.open))
          .sort((a, b) => a.time - b.time);
        chart = createChart(holder.current, {
          height: 390,
          layout: {
            background: { color: "#fff" },
            textColor: "#1f2937",
            fontFamily: "Inter,Arial,sans-serif",
            attributionLogo: false,
          },
          grid: {
            vertLines: { color: "#f4f5f7" },
            horzLines: { color: "#f4f5f7" },
          },
          rightPriceScale: { borderColor: "#e3e5e8" },
          timeScale: { borderColor: "#e3e5e8", timeVisible: true },
        });
        if (rows.length) {
          let c = chart.addSeries(CandlestickSeries, {
            upColor: "#059669",
            downColor: "#dc2626",
            wickUpColor: "#047857",
            wickDownColor: "#b91c1c",
            borderVisible: false,
          });
          c.setData(rows);
        }
        const probabilityHistory = probabilityPayload.history || [];
        if (market && probabilityHistory.length) {
          const line = chart.addSeries(LineSeries, {
            color: "#3867e8",
            lineWidth: 2,
            priceScaleId: "probability",
            priceFormat: {
              type: "custom",
              formatter: (v) => `${v.toFixed(0)}%`,
            },
          });
          line.setData(
            probabilityHistory.map((point) => ({
              time: Number(point.time),
              value: Number(point.probability) * 100,
            })),
          );
          chart.priceScale("probability").applyOptions({
            scaleMargins: { top: 0.12, bottom: 0.16 },
            borderVisible: false,
          });
          setHistoryStatus(
            `${probabilityHistory.length} live Polymarket points`,
          );
        } else {
          setHistoryStatus(
            market ? "Probability history unavailable" : "Price only",
          );
        }
        chart.timeScale().fitContent();
        observer = new ResizeObserver((e) =>
          chart?.applyOptions({ width: e[0].contentRect.width }),
        );
        observer.observe(holder.current);
      })
      .catch((e) => setError(e.message));
    return () => {
      cancel = true;
      observer?.disconnect();
      chart?.remove();
    };
  }, [symbol, tf, market?.id]);
  return (
    <section className="chart-panel">
      <header>
        <div>
          <span>{symbol}</span>
          <b>{market ? market.question : "Market price workspace"}</b>
        </div>
        <div className="legend">
          <span>
            <i />
            Price
          </span>
          {market && (
            <span>
              <i className="blue" />
              Probability
            </span>
          )}
        </div>
        <div className="chart-controls">
          <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
            {symbols.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
          {["M15", "H1", "H4", "D1"].map((x) => (
            <button
              className={tf === x ? "active" : ""}
              onClick={() => setTf(x)}
              key={x}
            >
              {x}
            </button>
          ))}
        </div>
      </header>
      {error ? (
        <div className="chart-error">{error}</div>
      ) : (
        <div ref={holder} />
      )}{" "}
      {market && <footer>{historyStatus}</footer>}
    </section>
  );
}
function Watchlist({ markets, selected, onSelect }) {
  return (
    <section className="watch">
      <header>
        <span>
          WATCHLIST<b>Event markets</b>
        </span>
      </header>
      {markets.map((m) => (
        <button
          className={selected?.id === m.id ? "active" : ""}
          onClick={() => onSelect(m)}
          key={m.id}
        >
          <span>
            <i>{m.category}</i>
            {m.question}
          </span>
          <b>
            {percentage(m.yes_probability)}
            <em className={m.change_24h >= 0 ? "up" : "down"}>
              {percentage(m.change_24h, true)}
            </em>
          </b>
        </button>
      ))}
    </section>
  );
}
function Agents({ market, latestRun, onRun, busy, error }) {
  return (
    <div className="agents">
      <header>
        <b>OpenAI research pipeline</b>
        <span>Paper only</span>
      </header>
      {latestRun ? (
        <div className="agent-result">
          <div>
            <b>{latestRun.decision.replace("_", " ")}</b>
            <span>{latestRun.confidence}% confidence</span>
          </div>
          <p>{latestRun.summary}</p>
          <section>
            <b>Bull case</b>
            <p>{latestRun.bull_case}</p>
          </section>
          <section>
            <b>Bear case</b>
            <p>{latestRun.bear_case}</p>
          </section>
          <section>
            <b>Risks</b>
            <ul>
              {latestRun.risks.map((risk) => (
                <li key={risk}>{risk}</li>
              ))}
            </ul>
          </section>
          <small>
            {latestRun.model} · {date(latestRun.created_at)}
          </small>
        </div>
      ) : (
        <p>
          Run a live research brief for this market. The service checks the bull
          case, bear case and key risks.
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
      <button
        className="agent-run"
        disabled={busy || !market}
        onClick={() => onRun(market)}
      >
        {busy
          ? "Research in progress"
          : latestRun
            ? "Run a fresh brief"
            : "Run research agents"}
      </button>
      <p>No live execution. Nautilus remains disconnected.</p>
    </div>
  );
}
function Intel({
  market,
  onTrade,
  mode,
  latestRun,
  onRunAgent,
  agentBusy,
  agentError,
}) {
  const [tab, setTab] = useState("milo");
  if (!market) return null;
  return (
    <section className="intel">
      <header>
        <button
          className={tab === "milo" ? "active" : ""}
          onClick={() => setTab("milo")}
        >
          Milo
        </button>
        <button
          className={tab === "agents" ? "active" : ""}
          onClick={() => setTab("agents")}
        >
          Trading agents
        </button>
      </header>
      {tab === "agents" ? (
        <Agents
          market={market}
          latestRun={latestRun}
          onRun={onRunAgent}
          busy={agentBusy}
          error={agentError}
        />
      ) : (
        <div className="brief">
          <div className="brief-title">
            <span>
              <Icon name="spark" />
            </span>
            <b>
              Decision brief<em>Evidence-led analysis</em>
            </b>
          </div>
          <h3>{market.question}</h3>
          <div className="model">
            <span>
              Market<b>{percentage(market.yes_probability)}</b>
            </span>
            <span>
              Momentum<b>{percentage(market.insight.momentum_probability)}</b>
            </span>
            <span>
              Shift
              <b className={market.insight.momentum_edge >= 0 ? "up" : "down"}>
                {percentage(market.insight.momentum_edge, true)}
              </b>
            </span>
          </div>
          <p>{market.insight.summary}</p>
          <div className="confidence">
            <span>
              Data confidence <b>{market.insight.confidence_score}/100</b>
            </span>
            <i>
              <em style={{ width: `${market.insight.confidence_score}%` }} />
            </i>
          </div>
          <Tags items={market.related_assets} />
          <button onClick={() => onTrade(market)}>Open paper order</button>
          <small>
            {mode === "live"
              ? "Live probability. Paper execution only."
              : "Illustrative probability. Paper execution only."}
          </small>
        </div>
      )}
    </section>
  );
}
function Portfolio({ portfolio, onClosePosition }) {
  return (
    <div className="table drawer-table">
      <table>
        <thead>
          <tr>
            <th>Market</th>
            <th>Side</th>
            <th>Contracts</th>
            <th>Average</th>
            <th>Mark</th>
            <th>Value</th>
            <th>P&amp;L</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {portfolio.positions.length ? (
            portfolio.positions.map((p) => (
              <tr key={p.id}>
                <td>
                  <b>{p.market_title}</b>
                </td>
                <td>{p.outcome}</td>
                <td>{p.quantity}</td>
                <td>{percentage(p.average_price)}</td>
                <td>{percentage(p.mark_price)}</td>
                <td>{money(p.value_cents)}</td>
                <td className={p.unrealised_pnl_cents >= 0 ? "up" : "down"}>
                  {money(p.unrealised_pnl_cents)}
                </td>
                <td>
                  <PositionClose position={p} onClose={onClosePosition} />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="8" className="empty">
                No open positions. Start with a paper order.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
function PositionClose({ position, onClose }) {
  const [confirming, setConfirming] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    if (!confirming) return undefined;
    const timer = window.setTimeout(() => setConfirming(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirming]);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onClose(position);
    } catch (requestError) {
      setError(requestError.message);
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {confirming ? (
        <span className="row-actions">
          <button disabled={busy} onClick={submit}>
            {busy ? "Closing" : "Confirm?"}
          </button>
          <button disabled={busy} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </span>
      ) : (
        <button
          disabled={busy}
          onClick={() => {
            setError("");
            setConfirming(true);
          }}
        >
          Close
        </button>
      )}
      {error && <small className="close-error">{error}</small>}
    </>
  );
}
function Drawer({ portfolio, agentRuns = [], alerts = [], onClosePosition }) {
  const [tab, setTab] = useState("Positions");
  let tabs = ["Positions", "Orders", "Agent decisions", "Alerts", "Activity"];
  const activity = [
    ...portfolio.recent_trades.map((trade) => ({
      id: `trade-${trade.id}`,
      type: "Paper order",
      title: `${trade.outcome} · ${trade.market_title}`,
      detail: `${trade.side === "sell" ? "Sell (close)" : "Buy"} · ${trade.quantity} contracts at ${percentage(trade.price)}`,
      created_at: trade.executed_at,
    })),
    ...agentRuns.map((run) => ({
      id: `agent-${run.id}`,
      type: "Agent decision",
      title: run.market_title,
      detail: `${run.decision.replace("_", " ")} · ${run.confidence}% confidence`,
      created_at: run.created_at,
    })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return (
    <section className="drawer">
      <nav>
        {tabs.map((x) => (
          <button
            className={tab === x ? "active" : ""}
            onClick={() => setTab(x)}
            key={x}
          >
            {x}
            {x === "Positions" && <b>{portfolio.positions.length}</b>}
            {x === "Orders" && <b>{portfolio.recent_trades.length}</b>}
            {x === "Agent decisions" && <b>{agentRuns.length}</b>}
            {x === "Alerts" && <b>{alerts.length}</b>}
          </button>
        ))}
      </nav>
      {tab === "Positions" && (
        <Portfolio portfolio={portfolio} onClosePosition={onClosePosition} />
      )}
      {tab === "Orders" && (
        <RecordList
          records={portfolio.recent_trades.map((trade) => ({
            id: trade.id,
            title: trade.market_title,
            label: `${trade.side === "sell" ? "Sell (close)" : "Buy"} · ${trade.outcome} · ${trade.quantity} contracts`,
            detail: `${percentage(trade.price)} · ${money(trade.amount_cents ?? trade.cost_cents)}`,
            created_at: trade.executed_at,
          }))}
          empty="No filled paper orders."
        />
      )}
      {tab === "Agent decisions" && (
        <RecordList
          records={agentRuns.map((run) => ({
            id: run.id,
            title: run.market_title,
            label: `${run.decision.replace("_", " ")} · ${run.confidence}%`,
            detail: run.summary,
            created_at: run.created_at,
          }))}
          empty="No agent research has been run."
        />
      )}
      {tab === "Alerts" && (
        <RecordList
          records={alerts.map((alert) => ({
            id: alert.id,
            title: `${alert.symbol} · ${alert.timeframe}`,
            label: `${alert.direction} · ${alert.confidence}%`,
            detail: alert.headline || "Signal Lab trade candidate",
            created_at: alert.created_at,
          }))}
          empty="No current Signal Lab alerts."
        />
      )}
      {tab === "Activity" && (
        <RecordList
          records={activity.map((item) => ({ ...item, label: item.type }))}
          empty="No recent activity."
        />
      )}
    </section>
  );
}
function RecordList({ records, empty }) {
  if (!records.length)
    return (
      <div className="drawer-empty">
        <span>{empty}</span>
      </div>
    );
  return (
    <div className="record-list">
      {records.map((record) => (
        <article key={record.id}>
          <div>
            <small>{record.label}</small>
            <b>{record.title}</b>
            <p>{record.detail}</p>
          </div>
          <time>{date(record.created_at)}</time>
        </article>
      ))}
    </div>
  );
}
function Metric({ label, value, note, tone = "" }) {
  return (
    <article className={`metric ${tone}`}>
      <span>{label}</span>
      <b>{value}</b>
      <em>{note}</em>
    </article>
  );
}

function Intelligence({ page, initialWatchlists = [], alertNotifications = [] }) {
  const [params] = useSearchParams(),
    [data, setData] = useState(null),
    [selected, setSelected] = useState(null),
    [order, setOrder] = useState(null),
    [error, setError] = useState(""),
    [agentBusy, setAgentBusy] = useState(false),
    [agentError, setAgentError] = useState(""),
    [watchlists, setWatchlists] = useState(initialWatchlists),
    [watchError, setWatchError] = useState(""),
    [marketAlerts, setMarketAlerts] = useState(null),
    [marketAlertsError, setMarketAlertsError] = useState("");
  const watchSeeded = useRef(false);
  useEffect(() => {
    if (watchSeeded.current || !initialWatchlists?.length) return;
    watchSeeded.current = true;
    setWatchlists((current) => (current.length ? current : initialWatchlists));
  }, [initialWatchlists]);
  const load = () =>
    get("/frontend-api/intelligence")
      .then((v) => {
        v.board.markets.forEach((m) =>
          marketNames.set(String(m.id), m.question),
        );
        setData(v);
        setSelected((s) => s || v.board.markets[0]);
      })
      .catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (page !== "markets") return;
    get("/frontend-api/alerts")
      .then(setMarketAlerts)
      .catch(() =>
        setMarketAlertsError("Probability alerts could not be loaded."),
      );
  }, [page]);
  if (error) return <PageError message={error} />;
  if (!data) return <Loading />;
  let {
      board,
      portfolio,
      agent_runs: agentRuns = [],
      alerts = [],
      paper_fee_bps: paperFeeBps = 10,
    } = data,
    q = (params.get("search") || "").toLowerCase(),
    cat = params.get("category"),
    filtered = board.markets.filter(
      (m) =>
        (!q ||
          `${m.question} ${m.category} ${m.related_assets.join(" ")}`
            .toLowerCase()
            .includes(q)) &&
        (!cat || m.category.toLowerCase() === cat.toLowerCase()),
    ),
    markets = filtered,
    activeMarket =
      markets.find((market) => market.id === selected?.id) ||
      markets[0] ||
      null,
    latestRun =
      agentRuns.find((run) => run.market_id === activeMarket?.id) || null,
    decoratedAlerts =
      marketAlerts === null
        ? null
        : marketAlerts.map((alert) => ({
            ...alert,
            // API-gap heuristic: no deactivation reason column, so an inactive
            // alert with a recent notification is shown as "market unavailable"
            // (a manually paused alert with a recent trigger can false-positive).
            unavailable:
              !alert.active &&
              alertNotifications.some(
                (notice) => notice.alert_id === alert.id,
              ),
          }));
  const done = (v) => {
    setData((d) => ({ ...d, portfolio: v.portfolio }));
    setOrder(null);
  };
  const closePosition = async (position) => {
    const result = await post(
      `/frontend-api/paper-positions/${position.id}/close`,
    );
    setData((d) => ({ ...d, portfolio: result.portfolio }));
  };
  const hasItem = (list, marketId) =>
    list.items.some((item) => String(item.market_id) === String(marketId));
  const addWatchItem = async (list, marketId) => {
    setWatchError("");
    setWatchlists((lists) =>
      lists.map((l) =>
        l.id === list.id
          ? {
              ...l,
              items: [
                ...l.items,
                { market_id: marketId, sort_order: l.items.length },
              ],
            }
          : l,
      ),
    );
    try {
      await post(`/frontend-api/watchlists/${list.id}/items`, {
        market_id: marketId,
      });
    } catch (requestError) {
      if (requestError.status === 422) return;
      setWatchlists((lists) =>
        lists.map((l) =>
          l.id === list.id
            ? {
                ...l,
                items: l.items.filter(
                  (item) => String(item.market_id) !== String(marketId),
                ),
              }
            : l,
        ),
      );
      setWatchError(requestError.message);
    }
  };
  const createWatchlist = async (name, marketId = null) => {
    const title = name.trim();
    if (!title) return;
    setWatchError("");
    const temp = {
      id: `temp-${Date.now()}`,
      name: title,
      sort_order: watchlists.length,
      items: [],
    };
    setWatchlists((lists) => [...lists, temp]);
    try {
      const created = await post("/frontend-api/watchlists", { name: title });
      const list = { ...created, items: created.items || [] };
      setWatchlists((lists) =>
        lists.map((l) => (l.id === temp.id ? list : l)),
      );
      if (marketId !== null) await addWatchItem(list, marketId);
    } catch (requestError) {
      setWatchlists((lists) => lists.filter((l) => l.id !== temp.id));
      setWatchError(requestError.message);
    }
  };
  const renameWatchlist = async (list, name) => {
    const title = name.trim();
    if (isTempWatchlist(list.id) || !title || title === list.name) return;
    setWatchError("");
    setWatchlists((lists) =>
      lists.map((l) => (l.id === list.id ? { ...l, name: title } : l)),
    );
    try {
      await patch(`/frontend-api/watchlists/${list.id}`, { name: title });
    } catch (requestError) {
      setWatchlists((lists) =>
        lists.map((l) => (l.id === list.id ? { ...l, name: list.name } : l)),
      );
      setWatchError(requestError.message);
    }
  };
  const deleteWatchlist = async (list) => {
    if (isTempWatchlist(list.id)) return;
    setWatchError("");
    setWatchlists((lists) => lists.filter((l) => l.id !== list.id));
    try {
      await del(`/frontend-api/watchlists/${list.id}`);
    } catch (requestError) {
      setWatchlists((lists) =>
        [...lists, list].sort((a, b) => a.sort_order - b.sort_order),
      );
      setWatchError(requestError.message);
    }
  };
  const removeWatchItem = async (list, marketId) => {
    if (isTempWatchlist(list.id)) return;
    setWatchError("");
    const removed = list.items.find(
      (item) => String(item.market_id) === String(marketId),
    );
    setWatchlists((lists) =>
      lists.map((l) =>
        l.id === list.id
          ? {
              ...l,
              items: l.items.filter(
                (item) => String(item.market_id) !== String(marketId),
              ),
            }
          : l,
      ),
    );
    try {
      await del(
        `/frontend-api/watchlists/${list.id}/items/${encodeURIComponent(marketId)}`,
      );
    } catch (requestError) {
      if (removed)
        setWatchlists((lists) =>
          lists.map((l) =>
            l.id === list.id ? { ...l, items: [...l.items, removed] } : l,
          ),
        );
      setWatchError(requestError.message);
    }
  };
  const toggleWatch = (market, list) => {
    if (!list) {
      if (!watchlists.length) return createWatchlist("Watchlist", market.id);
      list = watchlists[0];
    }
    if (isTempWatchlist(list.id)) return;
    return hasItem(list, market.id)
      ? removeWatchItem(list, market.id)
      : addWatchItem(list, market.id);
  };
  const jumpToMarket = (market) => {
    if (!markets.some((m) => String(m.id) === String(market.id))) return;
    setSelected(market);
    window.setTimeout(() => {
      document
        .querySelector(".table tr.selected")
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
  };
  const createAlert = async (market, condition, threshold) => {
    const created = await post("/frontend-api/alerts", {
      market_id: market.id,
      condition,
      threshold,
    });
    setMarketAlerts((current) => [...(current || []), created]);
  };
  const toggleAlert = async (alert) => {
    const updated = await patch(`/frontend-api/alerts/${alert.id}`, {
      active: !alert.active,
    });
    setMarketAlerts((current) =>
      (current || []).map((a) => (a.id === alert.id ? updated : a)),
    );
  };
  const deleteAlert = async (alert) => {
    setMarketAlerts((current) =>
      (current || []).filter((a) => a.id !== alert.id),
    );
    try {
      await del(`/frontend-api/alerts/${alert.id}`);
    } catch (requestError) {
      setMarketAlerts((current) => [...(current || []), alert]);
      throw requestError;
    }
  };
  const runAgent = async (market) => {
    setAgentBusy(true);
    setAgentError("");
    try {
      const result = await post("/frontend-api/agents/run", {
        market_id: market.id,
      });
      setData((current) => ({
        ...current,
        agent_runs: [result.run, ...(current.agent_runs || [])],
      }));
    } catch (requestError) {
      setAgentError(requestError.message);
    } finally {
      setAgentBusy(false);
    }
  };
  return (
    <>
      <div className="page">
        {page === "dashboard" && (
          <>
            <PageHead
              eyebrow="INTELLIGENCE DESK"
              title="Your market view is ready."
              text="Probability, price and risk in one controlled workspace."
            >
              <Mode board={board} />
              <button
                disabled={!activeMarket}
                onClick={() => setOrder(activeMarket)}
              >
                New paper order
              </button>
            </PageHead>
            <section className="card opportunities">
              <header>
                <span>
                  {board.mode === "live"
                    ? "LIVE OPPORTUNITIES"
                    : "SIMULATED OPPORTUNITIES"}
                  <b>Markets with the largest decision value</b>
                </span>
                <Link to="/prediction-markets">
                  View all markets <Icon name="arrow" />
                </Link>
              </header>
              <MarketTable
                markets={markets.slice(0, 4)}
                selected={activeMarket}
                onSelect={setSelected}
                onTrade={setOrder}
              />
            </section>
            <div className="workspace">
              <Watchlist
                markets={markets}
                selected={activeMarket}
                onSelect={setSelected}
              />
              <WorkChart market={activeMarket} />
              <Intel
                market={activeMarket}
                onTrade={setOrder}
                mode={board.mode}
                latestRun={latestRun}
                onRunAgent={runAgent}
                agentBusy={agentBusy}
                agentError={agentError}
              />
            </div>
            <Drawer
              portfolio={portfolio}
              agentRuns={agentRuns}
              alerts={alerts}
              onClosePosition={closePosition}
            />
          </>
        )}
        {page === "markets" && (
          <>
            <PageHead
              eyebrow="PROBABILITY ENGINE"
              title="Find the gap between expectation and evidence."
              text="Discover event markets. Link them to the assets you trade."
            >
              <Mode board={board} />
            </PageHead>
            <div className="filter">
              <div>
                <Link className={!cat ? "active" : ""} to="/prediction-markets">
                  All
                </Link>
                {[...new Set(board.markets.map((m) => m.category))].map((x) => (
                  <Link
                    className={cat === x ? "active" : ""}
                    key={x}
                    to={`/prediction-markets?category=${x}`}
                  >
                    {x}
                  </Link>
                ))}
              </div>
              <span>{markets.length} markets</span>
            </div>
            <WatchlistPanel
              watchlists={watchlists}
              markets={board.markets}
              visible={markets}
              onJump={jumpToMarket}
              onCreate={createWatchlist}
              onRename={renameWatchlist}
              onDelete={deleteWatchlist}
              onRemove={removeWatchItem}
              error={watchError}
            />
            <section className="card">
              {markets.length ? (
                <MarketTable
                  markets={markets}
                  selected={activeMarket}
                  onSelect={setSelected}
                  onTrade={setOrder}
                  watchlists={watchlists}
                  onWatch={toggleWatch}
                  alerts={decoratedAlerts}
                  alertsError={marketAlertsError}
                  onCreateAlert={createAlert}
                  onToggleAlert={toggleAlert}
                  onDeleteAlert={deleteAlert}
                />
              ) : (
                <div className="drawer-empty">
                  <span>No markets match this search.</span>
                </div>
              )}
            </section>
            <div className="analysis">
              <WorkChart market={activeMarket} />
              <Intel
                market={activeMarket}
                onTrade={setOrder}
                mode={board.mode}
                latestRun={latestRun}
                onRunAgent={runAgent}
                agentBusy={agentBusy}
                agentError={agentError}
              />
            </div>
          </>
        )}
        {page === "paper" && (
          <>
            <PageHead
              eyebrow="PAPER PORTFOLIO"
              title="Measure the decision before risking capital."
              text="All activity in this workspace is simulated."
            >
              <button
                disabled={!activeMarket}
                onClick={() => setOrder(activeMarket)}
              >
                New paper order
              </button>
            </PageHead>
            <div className="metrics">
              <Metric
                label="Account equity"
                value={money(portfolio.equity_cents)}
                note="Cash and positions"
              />
              <Metric
                label="Available cash"
                value={money(portfolio.cash_balance_cents)}
                note="Ready for new tests"
              />
              <Metric
                label="Open positions"
                value={portfolio.open_positions}
                note={money(portfolio.positions_value_cents)}
              />
              <Metric
                label="Total P&L"
                value={money(portfolio.total_pnl_cents)}
                note="Paper performance"
                tone={portfolio.total_pnl_cents >= 0 ? "positive" : "negative"}
              />
              {portfolio.realized_pnl_cents ? (
                <Metric
                  label="Realized P&L"
                  value={money(portfolio.realized_pnl_cents)}
                  note="Closed paper trades"
                  tone={
                    portfolio.realized_pnl_cents >= 0 ? "positive" : "negative"
                  }
                />
              ) : null}
            </div>
            <Drawer
              portfolio={portfolio}
              agentRuns={agentRuns}
              alerts={alerts}
              onClosePosition={closePosition}
            />
            <section className="card paper-list">
              <header>
                <span>
                  NEW IDEA<b>Choose a market to test</b>
                </span>
              </header>
              <MarketTable
                markets={markets}
                selected={activeMarket}
                onSelect={setSelected}
                onTrade={setOrder}
              />
            </section>
          </>
        )}
      </div>
      {order && (
        <Order
          market={order}
          mode={board.mode}
          close={() => setOrder(null)}
          filled={done}
          feeBps={paperFeeBps}
        />
      )}
    </>
  );
}
function PageHead({ eyebrow, title, text, children }) {
  return (
    <header className="page-head">
      <div>
        <span>{eyebrow}</span>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
      <section>{children}</section>
    </header>
  );
}
function Order({ market, mode, close, filled, feeBps }) {
  const [outcome, setOutcome] = useState("yes"),
    [quantity, setQuantity] = useState(100),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    const handleKey = (event) => event.key === "Escape" && close();
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [close]);
  let estimate = orderEstimate(
    quantity,
    market[`${outcome}_probability`],
    feeBps,
  );
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      filled(
        await post("/frontend-api/paper-orders", {
          market_id: market.id,
          outcome,
          quantity: +quantity,
        }),
      );
    } catch (x) {
      setError(x.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="order" onMouseDown={close} role="presentation">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="paper-order-title"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <header>
          <span>
            PAPER ORDER<b>Simulation only</b>
          </span>
          <button type="button" onClick={close}>
            ×
          </button>
        </header>
        <p className="order-mode">
          {mode === "live"
            ? "Live probability. Paper fill only."
            : "Illustrative probability. Paper fill only."}
        </p>
        <h2 id="paper-order-title">{market.question}</h2>
        <div className="outcomes">
          <button
            type="button"
            className={outcome === "yes" ? "active" : ""}
            onClick={() => setOutcome("yes")}
          >
            Yes <b>{percentage(market.yes_probability)}</b>
          </button>
          <button
            type="button"
            className={outcome === "no" ? "active no" : ""}
            onClick={() => setOutcome("no")}
          >
            No <b>{percentage(market.no_probability)}</b>
          </button>
        </div>
        <label>
          Contracts
          <input
            type="number"
            min="1"
            max="10000"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </label>
        <dl>
          <div>
            <dt>Estimated cost</dt>
            <dd>{money(estimate.totalCents)}</dd>
          </div>
          <div>
            <dt>Maximum payout</dt>
            <dd>{money(estimate.payoutCents)}</dd>
          </div>
        </dl>
        {error && <p className="form-error">{error}</p>}
        <button className="btn blue" disabled={busy}>
          {busy ? "Filling order" : `Paper buy ${outcome.toUpperCase()}`}
        </button>
        <small>No order reaches a live venue.</small>
      </form>
    </div>
  );
}

function ChartPage() {
  const [p] = useSearchParams(),
    s = (p.get("symbol") || "EURUSD").toUpperCase();
  const [markets, setMarkets] = useState([]);
  useEffect(() => {
    get("/frontend-api/intelligence")
      .then((v) => {
        (v.board?.markets || []).forEach((m) =>
          marketNames.set(String(m.id), m.question),
        );
        setMarkets(v.board?.markets || []);
      })
      .catch(() => {});
  }, []);
  return (
    <div className="page page-fullchart">
      <FullChart markets={markets} defaultSymbol={s} />
    </div>
  );
}
function Signals() {
  const [data, setData] = useState(null),
    [error, setError] = useState("");
  useEffect(() => {
    get("/frontend-api/signals")
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);
  if (error) return <PageError message={error} />;
  if (!data) return <Loading />;
  return (
    <SimplePage
      eyebrow="SIGNAL LAB"
      title="Find aligned market structure."
      text="Review direction, confidence and timeframe agreement."
      heads={[
        "Symbol",
        "Direction",
        "Confidence",
        "Average",
        "Candidates",
        "Timeframes",
        "Context",
      ]}
      rows={data.signals.map((s) => [
        s.symbol,
        s.direction,
        `${s.confidence}%`,
        `${s.average_confidence}%`,
        s.candidate_count,
        s.timeframes.join(", "),
        s.headline || "No headline",
      ])}
    />
  );
}
function Calendar() {
  const [data, setData] = useState(null),
    [error, setError] = useState("");
  useEffect(() => {
    get("/api/calendar/weekly")
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);
  if (error) return <PageError message={error} />;
  if (!data) return <Loading />;
  return (
    <SimplePage
      eyebrow="ECONOMIC CALENDAR"
      title="Know the next source of risk."
      text="High-impact releases for major currencies."
      heads={[
        "Time",
        "Currency",
        "Event",
        "Impact",
        "Actual",
        "Forecast",
        "Previous",
      ]}
      rows={data.events.map((e) => [
        date(e.event_time),
        e.currency,
        e.event,
        e.importance,
        e.actual || "Awaited",
        e.forecast || "n/a",
        e.previous || "n/a",
      ])}
    />
  );
}
function SimplePage({ eyebrow, title, text, heads, rows }) {
  return (
    <div className="page">
      <PageHead eyebrow={eyebrow} title={title} text={text} />
      <section className="card">
        <div className="table">
          <table>
            <thead>
              <tr>
                {heads.map((x) => (
                  <th key={x}>{x}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {r.map((x, j) => (
                    <td key={j}>{j === 0 ? <b>{x}</b> : x}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
function Milo() {
  const [data, setData] = useState(null),
    [prompt, setPrompt] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const load = () =>
    get("/frontend-api/milo")
      .then(setData)
      .catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);
  const ask = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      let id = data.active?.id;
      if (!id)
        id = (
          await post("/frontend-api/milo/conversations", {
            symbol: "EURUSD",
            timeframe: "H1",
          })
        ).id;
      await post(`/frontend-api/milo/${id}/messages`, { prompt });
      setPrompt("");
      await load();
    } catch (x) {
      setError(x.message);
    } finally {
      setBusy(false);
    }
  };
  if (!data) return error ? <PageError message={error} /> : <Loading />;
  return (
    <div className="page">
      <PageHead
        eyebrow="MILO MARKET AI"
        title="Ask for evidence. Not certainty."
        text="Milo reads market data, news, pivots and chart context."
      >
        <div className="credit">
          AI credit <b>{data.wallet.balance}</b>
        </div>
      </PageHead>
      <section className="milo">
        <aside>
          <b>Conversations</b>
          {data.conversations.map((x) => (
            <button key={x.id}>
              <span>
                {x.symbol} · {x.timeframe}
              </span>
              {x.title}
            </button>
          ))}
        </aside>
        <main>
          <div className="messages">
            {data.active?.messages?.length ? (
              data.active.messages.map((m) => (
                <article className={m.role} key={m.id}>
                  <span>{m.role === "assistant" ? "M" : "You"}</span>
                  <p>{m.content}</p>
                </article>
              ))
            ) : (
              <div className="empty-chat">
                <Icon name="spark" />
                <b>Start a market briefing</b>
                <span>
                  Ask about EURUSD, gold, crypto or an event scenario.
                </span>
              </div>
            )}
          </div>
          <form onSubmit={ask}>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="What changes if the Fed cuts rates before December?"
            />
            <button disabled={busy}>
              {busy ? "Reading the market" : "Ask Milo"}
            </button>
          </form>
          {error && <p className="form-error">{error}</p>}
        </main>
      </section>
    </div>
  );
}
function Protected({ bootstrap, inbox, setInbox, children }) {
  return bootstrap.authenticated ? (
    <Shell bootstrap={bootstrap} inbox={inbox} setInbox={setInbox}>
      {children}
    </Shell>
  ) : (
    <Navigate to="/login" replace />
  );
}
function App() {
  const [bootstrap, setBootstrap] = useState(null),
    [error, setError] = useState(""),
    [alertInbox, setAlertInbox] = useState({ unread: 0, items: [] });
  const load = () =>
    get("/frontend-api/bootstrap")
      .then((v) => {
        setCsrfToken(v.csrf_token);
        setBootstrap(v);
        setAlertInbox(v.alert_notifications || { unread: 0, items: [] });
      })
      .catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);
  if (error) return <PageError message={error} />;
  if (!bootstrap) return <Loading />;
  let protect = (x) => (
    <Protected bootstrap={bootstrap} inbox={alertInbox} setInbox={setAlertInbox}>
      {x}
    </Protected>
  );
  return (
    <Routes>
      <Route path="/" element={<PublicHome bootstrap={bootstrap} />} />
      <Route
        path="/login"
        element={
          bootstrap.authenticated ? (
            <Navigate to="/dashboard" />
          ) : (
            <AuthPage mode="login" onAuthenticated={load} />
          )
        }
      />
      <Route
        path="/register"
        element={
          bootstrap.authenticated ? (
            <Navigate to="/dashboard" />
          ) : (
            <AuthPage mode="register" onAuthenticated={load} />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          bootstrap.authenticated ? (
            <Navigate to="/dashboard" />
          ) : (
            <PasswordPage mode="forgot" />
          )
        }
      />
      <Route
        path="/reset-password"
        element={
          bootstrap.authenticated ? (
            <Navigate to="/dashboard" />
          ) : (
            <PasswordPage mode="reset" />
          )
        }
      />
      <Route
        path="/dashboard"
        element={protect(<Intelligence page="dashboard" />)}
      />
      <Route
        path="/prediction-markets"
        element={protect(
          <Intelligence
            page="markets"
            initialWatchlists={bootstrap.watchlists || []}
            alertNotifications={alertInbox.items}
          />,
        )}
      />
      <Route
        path="/paper-trading"
        element={protect(<Intelligence page="paper" />)}
      />
      <Route path="/charts" element={protect(<ChartPage />)} />
      <Route path="/signal-lab" element={protect(<Signals />)} />
      <Route path="/economic-calendar" element={protect(<Calendar />)} />
      <Route path="/milo" element={protect(<Milo />)} />
      <Route
        path="*"
        element={<Navigate to={bootstrap.authenticated ? "/dashboard" : "/"} />}
      />
    </Routes>
  );
}
createRoot(document.getElementById("root")).render(
  <BrowserRouter basename={basename}>
    <App />
  </BrowserRouter>,
);
